(ns ui.editor2.text
  (:require [clojure.set :as set]
            [clojure.walk :as walk]
            [datascript.core :as ds]
            ["prosemirror-state" :as prosemirror-state]
            ["prosemirror-view" :as prosemirror-view]
            ["prosemirror-model" :as prosemirror-model]
            ["prosemirror-keymap" :as prosemirror-keymap]
            ["prosemirror-schema-basic" :as prosemirror-schema-basic]
            ["prosemirror-commands" :as prosemirror-commands]
            ["prosemirror-history" :as prosemirror-history]
            [goog.color :as gcol]
            ))

;; We scale the text clip by this factor so that it has similar size to
;; the text of the prosemirror text editor.
(def text-clip-scale
  4.5)

(def pallet-colors
  ;; of Instagram
  ["#000000" ;white
   "#aaaaaa"
   "#ffffff"
   "#3897f1"
   "#70c04f"
   "#fdcb5c"
   "#fc8d33"
   "#ee4957"
   "#d1076a"

   "#a207ba"
   "#ed0014"
   "#ed858e"
   "#ffd2d4"
   "#fedbb3"
   "#ffc482"
   "#d29046"
   "#99643a"
   "#432326"
   "#1c4928"])

(def primary-fonts
  ["Classic"
   "Instagram Sans"
   "Instagram Sans Condensed"
   "Instagram Sans Script"
   "Modern"
   "Neon"
   "Bubble"
   "Typewriter"
   "Strong"
   "AvenyT"
   "Roboto"
   "San Francisco"])

(def standard-fonts
  ["Abril Fatface"
   "Acme"
   "Alegreya"
   "Alegreya Sans"
   "Amatic SC"
   "Anton"
   "Archivo Black"
   "Arial"
   "Arvo"
   "Assistant"
   "Bad Suabia Swing"
   "Bungee Shade"
   "Capsmall"
   "Chopin Script"
   "Cinzel"
   "Courier New"
   "Creepster"
   "Dancing Script"
   "Eater"
   "Ed Gein Gwilty"
   "Fjalla One"
   "Francois One"
   "Frijole"
   "Gloria Hallelujah"
   "Heebo"
   "Ice Caps"
   "Inconsolata"
   "Indie Flower"
   "Josefin Slab"
   "King Things"
   "Lobster"
   "Lobster Two"
   "Mountains"
   "Mystery Quest"
   "Nosifer"
   "Noto Sans"
   "Old Standard TT"
   "Open Sans Condensed"
   "Orbitron"
   "Pacifico"
   "Permanent Marker"
   "Pippi"
   "Poiret One"
   "Raleway"
   "Saira Condensed"
   "Times New Roman"
   "Ubuntu"
   "Yanone Kaffeesatz"])


(def shadows
  {"No Shadow" "none"
   "Light Shadow" "rgba(0, 0, 0, 0.2) 1px 1px 2px"
   "Medium Shadow" "rgba(0, 0, 0, 0.3) 2px 2px 4px"
   "Heavy Shadow" "rgba(0, 0, 0, 0.4) 3px 3px 6px"})

;; A simple text style mark that carries font family and font size attributes.
(def text-style-mark
  {:attrs #js {:fontFamily #js {:default "Roboto"}
               :fontSize #js {:default "16"}}
   :parseDOM #js [{:style "font-family" :get (fn [value] value)}
                  {:style "font-size" :get (fn [value] value)}]
   :toDOM (fn [node]
            (let [attrs (.-attrs node)]
              #js ["span" #js {"style" (str "font-family:" (.-fontFamily attrs)
                                            "; font-size:" (.-fontSize attrs))} 0]))})

;; Create a new schema that reuses the basic nodes and extends the marks.
(def default-line-height "1.2")

(def custom-schema
  (new prosemirror-model/Schema
       (clj->js {:nodes {"doc" {:content "block+"}
                         "text" {:group "inline"}
                         "paragraph" {:group "block"
                                      :attrs {:align {:default "left"}
                                              :lineHeight {:default default-line-height}}
                                      :content "inline*"
                                      :whitespace "pre"
                                      :parseDOM [{:tag "p"
                                                  :getAttrs (fn [node]
                                                              (clj->js {"align" (or (aget node "style" "text-align") "left")
                                                                        "lineHeight" (or (aget node "style" "line-height") default-line-height)}))}]

                                      :toDOM (fn [node]
                                               (let [align (aget node "attrs" "align")
                                                     line-height (aget node "attrs" "lineHeight")]
                                                 (clj->js ["p" {"style" (str "text-align:" align
                                                                              ";line-height:" line-height)} 0])))}

                         "hard_break" {:inline true
                                       :group "inline"
                                       :selectable false
                                       :parseDOM [{:tag "br"}]
                                       :toDOM (fn [node]
                                                #js ["br"])}}
                 :marks (js/Object.assign
                          #js {}
                          (aget prosemirror-schema-basic/schema "spec" "marks")
                          (clj->js {"strike" {:parseDOM [{"tag" "s"}]
                                              :toDOM (fn [] #js ["s" 0])}
                                    "italic" {:parseDOM [{"tag" "i"}]
                                              :toDOM (fn [] #js ["i" 0])}
                                    "fontFamily" {:attrs {:fontFamily {:default "Roboto"}}
                                                  :parseDOM [{:style "font-family" :get (fn [value] value)}]
                                                  :toDOM (fn [node]
                                                           (let [attrs (.-attrs node)]
                                                             #js ["span" #js {"style"
                                                                              (str "font-family:"
                                                                                   (.-fontFamily attrs)
                                                                                   ", 'Noto Color Emoji'")}
                                                                  0]))}
                                    "fontSize" {:attrs {:fontSize {:default "16"}}
                                                :parseDOM [{:style "font-size" :get (fn [value] value)}]
                                                :toDOM (fn [node]
                                                         (let [attrs (.-attrs node)]
                                                           #js ["span" #js {"style"
                                                                            (str "font-size:"
                                                                                 (.-fontSize attrs))}
                                                                0]))}
                                    "backgroundColor" {:attrs {:backgroundColor {:default "transparent"}}
                                                       :parseDOM [{:style "background-color"
                                                                   :get (fn [value] value)}]
                                                       :toDOM (fn [node]
                                                                (let [attrs (.-attrs node)]
                                                                  #js ["span" #js {"style"
                                                                                   (str "background-color:"
                                                                                        (.-backgroundColor attrs))} 0]))}
                                    "color" {:attrs {:color {:default "white"}}
                                             :parseDOM [{:style "color"
                                                         :get (fn [value] value)}]
                                             :toDOM (fn [node]
                                                      (let [attrs (.-attrs node)]
                                                        #js ["span" #js {"style"
                                                                         (str "color:"
                                                                              (.-color attrs))} 0]))}
                                    "underline" {:parseDOM [{"tag" "u"}]
                                                 :toDOM (fn [] #js ["u" 0])}
                                    "bold" {:parseDOM [{"tag" "b"}]
                                            :toDOM (fn [] #js ["b" 0])}
                                    }))})))

(def keep-marks-on-paste
  (prosemirror-state/Plugin.
    #js {:key (prosemirror-state/PluginKey. "keepMarksOnPlainPaste")
         :props
         #js {:handlePaste
              (fn [^js view _event ^js slice _]
                (let [state (.-state view)
                      marks (or (.-storedMarks state)
                                (.marks (.-$from (.-selection state))))
                      has-marks? (and marks (pos? (.-length marks)))]
                  (when has-marks?
                    (let [^js content (.-content slice)
                          text (.textBetween content 0 (.-size content) "\n" "\n")
                          schema (.-schema state)
                          lines (.split text #"\n")
                          paragraphs (clj->js
                                       (mapv (fn [line]
                                               (if (= line "")
                                                 (.node schema "paragraph")
                                                 (.node schema "paragraph" nil
                                                        #js [(.text schema line marks)])))
                                             lines))
                          fragment (.fromArray prosemirror-model/Fragment paragraphs)
                          slice' (prosemirror-model/Slice. fragment 1 1)
                          tr (.-tr state)]
                      (.dispatch view (.replaceSelection tr slice'))
                      true))))}}))

(defn add-brs-to-empty-ps
  "Walk a hiccup tree and add [:br] into every [:p] element that has no children."
  [hiccup]
  (let [children (fn [[tag & others :as node]]
                   ;; strip off the tag and possibly the attribute map which
                   ;; could be the first of `others`
                   (when (vector? node)
                     (let [children (if (map? (first others))
                                      (rest others)
                                      others)]
                       (seq children))))
        ;; find all :p nodes whose children list is empty:
        empty-ps (->> (tree-seq vector? children hiccup)
                      (filter (fn [node]
                                (and (vector? node)
                                     (= (first node) :p)
                                     (empty? (or (children node)
                                                 [])))))
                      set)]
    ;; now do one pass to replace each of those exact vectors with
    ;; (conj node [:br])
    (walk/prewalk
      (fn [node]
        (if (get empty-ps node)
          (conj node
                [:br])
          node))
      hiccup)))

(defn dom->hiccup
  "Converts a dom to hiccup without escaping text content."
  [node]
  (case (.-nodeType node)
    ;; 3 = TEXT_NODE
    3 (.-textContent node)

    ;; 1 = ELEMENT_NODE
    1 (let [tag (keyword (.toLowerCase (.-tagName node)))
            attrs (into {}
                        (for [attr (array-seq (.-attributes node))]
                          [(keyword (.-name attr)) (.-value attr)]))
            children (mapv dom->hiccup (array-seq (.-childNodes node)))]
        (if (empty? attrs)
          (into [tag] children)
          (into [tag attrs] children)))

    ;; default: ignore comment, document, etc.
    nil))

(defn get-doc-hiccup
  [^js doc]
  (let [serializer (.fromSchema (.-DOMSerializer prosemirror-model)
                                custom-schema)
        ;; Serialize the document node to a DOM fragment:
        fragment (.serializeFragment serializer
                                     doc)
        ;; Create a temporary div to hold the fragment:
        temp-div (.createElement js/document "div")]
    (.appendChild temp-div fragment)

    (-> (dom->hiccup temp-div)
        (assoc 0 :div)
        (add-brs-to-empty-ps))))

(defn children
  "Given a node, return its nested parts for tree-seq."
  [node]
  (cond
    (map? node)       (vals node)
    (sequential? node) node
    (set? node)       node
    :else             nil))

(defn max-font-size
  "Walks any nested Clojure data structure and returns the largest
   integer value found in any `:style` map entry of the form
   \"font-size:<number>\"."
  [root]
  (let [sizes (->> (tree-seq some? children root)
                   (filter map?)
                   (keep (fn [{:keys [style]}]
                           (when (string? style)
                             (when-let [[_ n]
                                        (re-find #"font-size\s*:\s*(\d+)" style)]
                               (js/parseInt n))))))]
    (when (seq sizes)
      (apply max sizes))))

(defn add-font-padding
  "Unfortunately, some fonts overshoot their html containers.
   There is no easy way to calculate the real bounding box of this.
   Therefore, we add some padding and cut away any text with overflow:hidden.
   The padding is calculated from the maximum font size in any descending
   element of the given hiccup."
  [hiccup]
  (let [max-font-size (max-font-size hiccup)]
    [:div {:style {:padding (str (* max-font-size 0.1) "px")
                   :overflow "hidden"
                   :white-space "pre-wrap"
                   :overflow-wrap "break-word"}} hiccup]))

(defn collect-marks-from-doc
  [^js doc]
  (let [seen (atom #{})]
    (.descendants doc
                  (fn [node _pos]
                    (when (.-isText node)
                      (doseq [m (array-seq (.-marks node))]
                        (swap! seen conj m)))))
    (vec @seen)))

(def keymap-plugin
  (prosemirror-keymap/keymap
    #js {"Backspace" (prosemirror-commands/chainCommands
                       prosemirror-commands/deleteSelection
                       prosemirror-commands/joinBackward)
         "Enter" (prosemirror-commands/chainCommands
                   prosemirror-commands/deleteSelection
                   prosemirror-commands/splitBlockKeepMarks)
         "Delete" (prosemirror-commands/chainCommands
                    prosemirror-commands/deleteSelection
                    prosemirror-commands/joinForward
                    prosemirror-commands/selectNodeForward)
         "Mod-z" prosemirror-history/undo
         "Mod-Shift-z" prosemirror-history/redo
         "Mod-y" prosemirror-history/redo}))

(defn init-prosemirror-editor!
  [state]
  ;; Destroy old prosemirror editor view to start a
  ;; fresh editor on every initialization:
  (when-let [^ĵs view (get-in state
                              [:editor2 :text-editor-view])]
    (.destroy view))

  (let [editor (:editor2 state)
        {:keys [selected-clip db]} editor
        clip (ds/entity db
                        [:clip/uuid selected-clip])
        saved-doc (get-in clip
                          [:clip/asset :asset/data :saved-doc])
        doc (if saved-doc
              (.nodeFromJSON custom-schema
                             (js/JSON.parse saved-doc))
              (.node custom-schema "doc"
                     nil
                     (clj->js [(.node custom-schema "paragraph" nil
                                      (.text custom-schema "Hello, ProseMirror with keymap!"))])))
        editor-state (prosemirror-state/EditorState.create (clj->js {:schema custom-schema
                                                                     :plugins [(prosemirror-history/history)
                                                                               keep-marks-on-paste
                                                                               keymap-plugin]
                                                                     :storedMarks (clj->js (collect-marks-from-doc doc))
                                                                     :doc doc}))
        editor-view (new prosemirror-view/EditorView
                         (.getElementById js/document "prosemirror-editor")
                         #js {:state editor-state
                              :dispatchTransaction (fn [tr]
                                                     (this-as self
                                                       (let [new-editor-state (.apply self.state tr)
                                                             ;; Unfortunately, it is necessary to stringify the json
                                                             ;; so that it does not contain any unwanted js objects
                                                             ;; and only pure data... (js->clj) did not work either.
                                                             doc (.-doc new-editor-state)
                                                             editor-state-json (js/JSON.stringify (.toJSON doc))
                                                             hiccup (get-doc-hiccup doc)]
                                                         (.updateState self new-editor-state)
                                                         ((:replicant/dispatch! state)
                                                          {:actions [[:store/assoc-in
                                                                      [:editor2 :text-editor-state]
                                                                      new-editor-state]
                                                                     [:editor2/update-text-asset!
                                                                      {:hiccup (add-font-padding hiccup)
                                                                       :text (.-textContent doc)
                                                                       :saved-doc editor-state-json}]
                                                                     ]}))))})]
    (update state
            :editor2
            merge
            {:text-editor-view editor-view
             :text-editor-state editor-state
             :init-text-editor? false})))

;; TODO, re-implement shadows
#_(defn shadow-menu
    [state]
    (let [shadow (get-in state
                         [:editor2 :quill/selected-formats "shadow"])
          shadow* (if (vector? shadow)
                    (first shadow)
                    shadow)]
      [:div.dropdown.dropdown-bottom.dropdown-end
       [:div.btn.btn-sm.bg-slate-800.text-white.w-40
        {:tabindex "3"
         :role "button"
         :style {:text-shadow shadow*}}
        [:div.overflow-hidden.text-ellipsis.whitespace-nowrap
         (or (some (fn [[k v]]
                     (when (= v shadow*) k))
                   shadows)
             "No Shadow")]
        [:svg {:class ["w-4" "h-4"]
               :xmlns "http://www.w3.org/2000/svg"
               :fill "none"
               :viewBox "0 0 24 24"
               :stroke "currentColor"}
         [:path {:stroke-linecap "round"
                 :stroke-linejoin "round"
                 :stroke-width "2"
                 :d "M19 9l-7 7-7-7"}]]]
       [:div.dropdown-content.bg-slate-600.rounded-box.z-1.w-52.p-2.shadow-sm.flex.flex-col
        {:tabindex "3"}
        (map (fn [[name value]]
               [:span.hover:bg-slate-700.cursor-pointer
                {:value value
                 :style {:text-shadow value}
                 :on {:click [[]]}}
                name])
             shadows)]]))

(def arrow-svg
  [:svg {:class "ml-2 h-5 w-5 text-gray-500"
         :xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 20 20"
         :fill "currentColor"
         :aria-hidden "true"}
   [:path {:fill-rule "evenodd"
           :d "M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
           :clip-rule "evenodd"}]])

(defn merge-maps-into-sets
  [set-map normal-map]
  (merge-with set/union
              set-map
              (into {}
                    (for [[k v]
                          normal-map]
                      [k #{v}]))))

(defn mark-to-map
  [^js mark]
  (let [n (.-name (.-type mark))
        attrs (js->clj (js/JSON.parse (js/JSON.stringify (.-attrs mark))))]
    (if (empty? attrs)
      {n true}
      attrs)))

(defn active-styles
  [state]
  (let [^js editor-state (get-in state
                                 [:editor2 :text-editor-state])
        sel (.-selection editor-state)
        from (.-from sel)
        to (.-to sel)]
    (if (.-empty sel)
      ;; For an empty selection, use storedMarks if present or the marks at the cursor.
      (let [marks (or (seq (set (map mark-to-map
                                     (.-storedMarks editor-state))))
                      (set (map
                             mark-to-map
                             (array-seq (.marks (.-$from sel))))))
            $from (.-$from sel)
            parent (.-parent $from)
            parent-attrs (when (.-isBlock (.-type parent))
                           (js->clj (js/Object.assign #js {} (.-attrs parent))))]
        {:marks (into {} marks)
         :attrs (into {} (map (fn [[k v]] [k #{v}]) parent-attrs))})
      ;; For a range selection, compute the intersection of marks in all nodes in the selection.
      (let [common-marks (atom nil)
            common-attrs (atom {})]
        (.nodesBetween (.-doc editor-state) from to
                       (fn [node _pos]
                         (when (.-isText (.-type node))
                           (let [node-marks (set (map
                                                   mark-to-map
                                                   (array-seq (.-marks node))))]
                             (reset! common-marks
                                     (if @common-marks
                                       (set/intersection @common-marks
                                                         node-marks)
                                       node-marks))))


                         (when (.-isBlock (.-type node))
                           (let [node-attrs (js->clj (js/Object.assign #js {}
                                                                       (.-attrs node)))]
                             (swap! common-attrs
                                    merge-maps-into-sets
                                    node-attrs)))))
        {:attrs @common-attrs
         :marks (into {} @common-marks)}))))

(defn readable-text-color
  "Pick black or white by HSL lightness."
  [hex]
  (let [[h s l] (gcol/hexToHsl hex)]
    (if (< l 0.5)
      "#ffffff"
      "#000000")))

(defn color-input-value
  [value fallback]
  (if (and (string? value)
           (re-matches #"#[0-9a-fA-F]{6}" value))
    value
    fallback))

(defn bg-color-menu
  [state]
  (let [selected-color (get-in (active-styles state)
                               [:marks "backgroundColor"])]
    (list
      [:div {:class ["relative" "inline-block" "w-12" "h-8"]}
       [:div
        {:class ["absolute" "inset-0"
                 "flex" "items-center" "justify-between"
                 "px-1" "py-2"
                 "bg-slate-800"
                 "text-white"
                 "border"
                 "border-gray-300"
                 "rounded-md"
                 "shadow-sm"
                 ;; Important:
                 "pointer-events-none"
                 "z-0"
                 ]}
        [:i.fa-solid.fa-highlighter
         {:style {:color selected-color}}]
        arrow-svg]
       [:select {:on {:change [[:editor2/set-text-mark-or-click! "backgroundColor"
                                {:backgroundColor :event/target.value}]]}
                 :value selected-color
                 :title "Select Background Color"
                 :class ["absolute" "inset-0"
                         "w-full" "h-full"
                         "opacity-0"
                         "appearance-none"
                         "cursor-pointer"
                         "z-10"
                         ]}
        [:option.text-black
         {:value "transparent"}
         "None"]
        [:option.text-black
         {:value {:click "#bg-color-input"}}
         "Custom"]
        (for [color pallet-colors]
          [:option
           {:value color
            :style {:color (readable-text-color color)
                    :background-color color}}
           color])]]
      [:input {:type "color"
               :value (color-input-value selected-color "#000000")
               :id "bg-color-input"
               :class "sr-only"
               :on {:click (fn [e]
                             (.stopPropagation e))
                    :input [[:editor2/set-text-mark! "backgroundColor"
                             {:backgroundColor :event/target.value}]]
                    :change [[:editor2/set-text-mark! "backgroundColor"
                              {:backgroundColor :event/target.value}]]}}])))

(defn font-color-menu
  [state]
  (let [selected-color (get-in (active-styles state)
                               [:marks "color"])]
    (list
      [:div {:class ["relative" "inline-block" "w-12" "h-8"]}
       [:div
        {:class ["absolute" "inset-0"
                 "flex" "items-center" "justify-between"
                 "px-1" "py-2"
                 "bg-slate-800"
                 "text-white"
                 "border"
                 "border-gray-300"
                 "rounded-md"
                 "shadow-sm"
                 ;; Important:
                 "pointer-events-none"
                 "z-0"
                 ]}
        [:i.fa-solid.fa-palette
         {:style {:color selected-color}}]
        arrow-svg]
       [:select {:on {:change [[:editor2/set-text-mark-or-click! "color"
                                {:color :event/target.value}]]}
                 :value selected-color
                 :title "Select Text Color"
                 :class ["absolute" "inset-0"
                         "w-full" "h-full"
                         "opacity-0"
                         "appearance-none"
                         "cursor-pointer"
                         "z-10"
                         ]}
        [:option.text-black
         {:value "inherit"}
         "None"]
        [:option.text-black
         {:value {:click "#font-color-input"}}
         "Custom"]
        (for [color pallet-colors]
          [:option
           {:value color
            :style {:color (readable-text-color color)
                    :background-color color}}
           color])]]
      [:input {:type "color"
               :id "font-color-input"
               :class "sr-only"
               :value (color-input-value selected-color "#ffffff")
               :on {:click (fn [e]
                             (.stopPropagation e))
                    :input [[:editor2/set-text-mark! "color"
                             {:color :event/target.value}]]
                    :change [[:editor2/set-text-mark! "color"
                              {:color :event/target.value}]]}}])))

(defn font-menu
  [state]
  (let [selected-font (get-in (active-styles state)
                              [:marks "fontFamily"])]
    [:select.bg-slate-700.border.rounded.overflow-hidden.text-ellipsis.whitespace-nowrap.w-full.h-8.text-sm.p-1.pr-6
     {:value selected-font
      :on {:change [[:editor2/set-text-mark!
                     "fontFamily"
                     {:fontFamily :event/target.value}]]}}
     (map (fn [font]
            [:option
             {:selected (= selected-font font)
              :style {:font-family font}}
             font])
          primary-fonts)
     [:hr.my-2]
     (map (fn [font]
            [:option
             {:selected (= selected-font font)
              :style {:font-family font}}
             font])
          standard-fonts)]))

(defn text-style-buttons
  [state]
  (let [active-marks (:marks (active-styles state))]
    (list
      [:button.p-2
       {:class (when (get active-marks "bold")
                 ["text-blue-500"])
        :on {:mousedown [[:editor2/toggle-text-mark! "bold"]]}}
       [:i.fas.fa-bold]]
      [:button.p-2
       {:class (when (get active-marks "italic")
                 ["text-blue-500"])
        :on {:mousedown [[:editor2/toggle-text-mark! "italic"]]}}
       [:i.fas.fa-italic]]
      [:button.p-2
       {:class (when (get active-marks "underline")
                 ["text-blue-500"])
        :on {:mousedown [[:editor2/toggle-text-mark! "underline"]]}}
       [:i.fas.fa-underline]]
      [:button.p-2
       {:class (when (get active-marks "strike")
                 ["text-blue-500"])
        :on {:mousedown [[:editor2/toggle-text-mark! "strike"]]}}
       [:i.fas.fa-strikethrough]])))

(defn alignment-buttons
  [state]
  (let [alignment (get-in (active-styles state)
                          [:attrs "align"])]
    (list
      [:button.p-2
       {:class (when (get alignment "left")
                 ["text-blue-500"])
        :on {:click [[:editor2/set-text-align! "left"]]}}
       [:i.fas.fa-align-left]]
      [:button.p-2
       {:class (when (get alignment "center")
                 ["text-blue-500"])
        :on {:click [[:editor2/set-text-align! "center"]]}}
       [:i.fas.fa-align-center]]
      [:button.p-2
       {:class (when (get alignment "right")
                 ["text-blue-500"])
        :on {:click [[:editor2/set-text-align! "right"]]}}
       [:i.fas.fa-align-right]])))

(defn font-size
  [state]
  [:span.flex.items-center.gap-1
   [:input.text-black.p-1.text-sm.bg-slate-700.text-white.border.rounded.border-white.w-14
    {:type "number"
     :value (get-in (active-styles state)
                    [:marks "fontSize"])
     :min 4
     :on {:change [[:editor2/set-text-mark!
                    "fontSize"
                    {:fontSize :event/target.value}]]}}]
   [:span.w-6.text-center "px"]])

(def line-height-options
  ["0.6" "0.7" "0.8" "0.9" "1.0" "1.1" "1.2" "1.3" "1.4" "1.5" "1.6" "1.8" "2.0" "2.5" "3.0"])

(defn line-height
  [state]
  (let [current (or (first (get-in (active-styles state)
                                   [:attrs "lineHeight"]))
                    default-line-height)]
    [:span.flex.items-center.gap-1
     [:select.bg-slate-700.border.rounded.text-white.text-sm.p-1.h-8.w-14
      {:value current
       :on {:change [[:editor2/set-text-line-height! :event/target.value]]}}
      (map (fn [v]
             [:option {:value v
                       :selected (= v current)}
              v])
           line-height-options)]
     [:i.fas.fa-arrows-up-down.text-xs.w-6.text-center]]))

(defn edit-panel
  [state]
  [:div
   {:replicant/key "text-edit-panel"
    :class ["flex" "flex-col" "gap-2" "w-full"]}
   [:span
    {:class ["text-xs" "font-medium"]}
    "Text"]
   ;; Remove the default editor border when focused.
   [:style ".ProseMirror-focused{outline: none;} .ProseMirror{white-space: pre-wrap;}"]
   (when (get-in state
                 [:editor2 :text-editor-state])
     [:div {:class ["grid" "grid-cols-2" "gap-x-4" "gap-y-2" "w-full"]}
      (font-menu state)
      (font-size state)
      [:div.flex
       (font-color-menu state)
       (bg-color-menu state)]
      (line-height state)
      [:div.flex
       (text-style-buttons state)]
      [:div.flex
       (alignment-buttons state)]])
   [:div
    {:id "prosemirror-editor"
     :class ["focus:border"
             "focus:border-black"
             "border-slate-300"
             "p-1"
             "rounded"
             "text-white"
             "border"
             "whitespace-pre-wrap"
             "break-all"]
     :style {:font-size "16px"
             :font-family "'Noto Color Emoji'"}}]
   [:p.text-gray-400.mt-1.font-mono
    {:style {:font-size "10px"}}
    "Undo: Ctrl+Z (Cmd+Z)" [:br]
    "Redo: Ctrl+Shift+Z (Cmd+Shift+Z)"]
   ])

